/**
 * Feedback dialog — sends the submission via POST /api/feedback.
 *
 * Category chips at the top, multiline message in the middle, follow-up
 * checkbox at the bottom.  After successful submit, shows a brief
 * "Thanks!" state before auto-closing.
 *
 * HY-themed throughout: warm ivory paper, gold accents, ink text — same
 * design system as the admin dialogs.
 */

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { submitFeedback, type FeedbackCategory } from '../../api/feedback';
import { useAuth } from '../../auth/useAuth';
import { FEEDBACK_CATEGORIES as CATEGORIES } from '../../lib/feedbackCategories';

const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const INK = '#0f0a07';
const IVORY = '#fdf9f0';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const MotionBox = motion.create(Box);

export function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const { user } = useAuth();
  const location = useLocation();
  const firstName = user?.first_name?.trim() || '';

  const [category, setCategory] = useState<FeedbackCategory>('idea');
  const [message, setMessage] = useState('');
  const [allowFollowup, setAllowFollowup] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset the form whenever the dialog opens — but slightly delay the
  // reset on close so the closing animation doesn't show a jumpy form.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => messageRef.current?.focus(), 140);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setCategory('idea');
      setMessage('');
      setAllowFollowup(true);
      setError(null);
      setSubmitted(false);
    }, 260);
    return () => clearTimeout(t);
  }, [open]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!message.trim()) {
      setError('Tell us a little more.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback({
        category,
        message: message.trim(),
        page_path: location.pathname,
        allow_followup: allowFollowup,
      });
      setSubmitted(true);
      setTimeout(onClose, 1600);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not send that. Try again?';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !submitting) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  // CATEGORIES is non-empty and `category` is constrained to its values
  // by the union type, so the lookup is always defined.
  const activeCategory = (CATEGORIES.find((c) => c.value === category) ?? CATEGORIES[0])!;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 10, 7, 0.42)',
            backdropFilter: 'blur(4px)',
          },
        },
        paper: {
          sx: {
            position: 'relative',
            bgcolor: IVORY,
            backgroundImage: 'none',
            border: '1px solid rgba(187, 140, 63, 0.22)',
            borderRadius: 2,
            m: { xs: 2, sm: 4 },
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
              zIndex: 1,
            },
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={submitting}
        size="small"
        aria-label="Close feedback"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'rgba(15, 10, 7, 0.45)',
          zIndex: 2,
          '&:hover': { color: GOLD, bgcolor: 'rgba(187, 140, 63, 0.08)' },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {submitted ? (
        <Box sx={{ textAlign: 'center', py: 6, px: 4 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
              color: INK,
              boxShadow: '0 10px 24px rgba(187, 140, 63, 0.28)',
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography
            sx={{
              mt: 2.25,
              fontFamily: FONT_INTER,
              fontSize: 18,
              fontWeight: 700,
              color: INK,
            }}
          >
            Got it — thank you!
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: FONT_INTER,
              fontSize: 13,
              color: 'rgba(15, 10, 7, 0.6)',
            }}
          >
            Every note shapes what we ship next.
          </Typography>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} sx={{ px: { xs: 2.5, sm: 4 }, py: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25, pr: 4 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
                color: INK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 18,
                fontWeight: 700,
                color: INK,
                letterSpacing: '-0.01em',
              }}
            >
              Tell us what you think
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13.5,
              color: 'rgba(15, 10, 7, 0.62)',
              lineHeight: 1.55,
            }}
          >
            {firstName ? (
              <>
                Thanks for using the platform,{' '}
                <Box component="span" sx={{ color: GOLD, fontWeight: 700 }}>
                  {firstName}
                </Box>
                .
              </>
            ) : (
              <>Thanks for using the platform.</>
            )}{' '}
            Every note shapes what we ship next.
          </Typography>

          {/* Category chips */}
          <Typography
            sx={{
              mt: 3,
              mb: 1.25,
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: GOLD,
            }}
          >
            Category
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: { xs: 0.75, sm: 1.25 },
            }}
          >
            {CATEGORIES.map((c) => {
              const active = c.value === category;
              return (
                <MotionBox
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  role="button"
                  aria-pressed={active}
                  whileTap={{ scale: 0.97 }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    py: { xs: 1.5, sm: 1.75 },
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    border: '1.5px solid',
                    borderColor: active ? c.activeBorder : 'rgba(15, 10, 7, 0.12)',
                    bgcolor: active ? c.activeBg : '#fff',
                    color: active ? c.activeText : 'rgba(15, 10, 7, 0.55)',
                    fontFamily: FONT_INTER,
                    fontWeight: 700,
                    fontSize: 12,
                    transition:
                      'background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease',
                    userSelect: 'none',
                    '&:hover': {
                      bgcolor: active ? c.activeBg : 'rgba(187, 140, 63, 0.06)',
                      borderColor: active ? c.activeBorder : 'rgba(187, 140, 63, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: { xs: 22, sm: 24 },
                      lineHeight: 1,
                      transform: active ? 'scale(1.05)' : 'scale(1)',
                      transition: 'transform 200ms ease',
                    }}
                    aria-hidden
                  >
                    {c.emoji}
                  </Box>
                  <Box sx={{ fontSize: { xs: 12, sm: 13 } }}>{c.label}</Box>
                </MotionBox>
              );
            })}
          </Box>

          {/* Message */}
          <Typography
            sx={{
              mt: 3,
              mb: 1.25,
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: GOLD,
            }}
          >
            Your message
          </Typography>
          <TextField
            inputRef={messageRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={activeCategory.placeholder}
            multiline
            minRows={5}
            maxRows={10}
            fullWidth
            inputProps={{ maxLength: 2000 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 250, 235, 0.55)',
                fontFamily: FONT_INTER,
                fontSize: 14,
                lineHeight: 1.6,
                transition: 'background-color 160ms ease',
                '& fieldset': { borderColor: 'rgba(15, 10, 7, 0.14)' },
                '&:hover': { bgcolor: 'rgba(255, 250, 235, 0.85)' },
                '&:hover fieldset': { borderColor: 'rgba(187, 140, 63, 0.4)' },
                '&.Mui-focused': { bgcolor: '#fffdf6' },
                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1.5 },
              },
              '& textarea::placeholder': { color: 'rgba(15, 10, 7, 0.4)', opacity: 1 },
            }}
          />
          <Box
            sx={{
              mt: 0.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: 16,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11,
                color: message.length > 1800 ? '#b22f2f' : 'rgba(15, 10, 7, 0.42)',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {message.length} / 2000
            </Typography>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 1.25 }}>
              {error}
            </Alert>
          )}

          {/* Follow-up checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={allowFollowup}
                onChange={(e) => setAllowFollowup(e.target.checked)}
                size="small"
                sx={{
                  color: 'rgba(15, 10, 7, 0.35)',
                  '&.Mui-checked': { color: GOLD },
                  p: 0.5,
                }}
              />
            }
            label={
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 12, color: 'rgba(15, 10, 7, 0.62)' }}
              >
                It's OK to email me back about this
              </Typography>
            }
            sx={{ mt: 1.5, ml: -0.5 }}
          />

          {/* Actions */}
          <Box
            sx={{
              mt: 2.5,
              pt: 2.25,
              borderTop: '1px dashed rgba(187, 140, 63, 0.22)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              onClick={onClose}
              disabled={submitting}
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'rgba(15, 10, 7, 0.6)',
                '&:hover': { bgcolor: 'rgba(15, 10, 7, 0.04)' },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !message.trim()}
              startIcon={!submitting ? <SendIcon sx={{ fontSize: 15 }} /> : undefined}
              sx={{
                background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
                color: INK,
                fontFamily: FONT_INTER,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontSize: 12,
                px: 2.5,
                py: 0.85,
                boxShadow: 'none',
                borderRadius: 1,
                '&:hover': {
                  background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                  boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
                },
                '&.Mui-disabled': {
                  background: 'rgba(15, 10, 7, 0.08)',
                  color: 'rgba(15, 10, 7, 0.3)',
                },
              }}
            >
              {submitting ? <CircularProgress size={16} sx={{ color: INK }} /> : 'Send feedback'}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
